import classNames from "classnames";
import React, { FC, KeyboardEvent, memo, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { Text } from "@/components/Text";
import { ITypeaheadProps } from "@/components/Typeahead";
import { UserContext } from "@/components/User";
import {
  addKeywordSearchHistory,
  deleteKeywordSearchHistory,
  fetchKeywordsSearchHistory,
  IKeyword,
} from "@/data/keywords";
import { Flex, SelectComponents, TextInput } from "@/eworkitComponents";
import { useToggle } from "@/hooks/Toggle";
import { IFilterComponentProps, useOnChangeFilterHandler } from "@/infrastructure/Filter";
import { SessionContext } from "@/infrastructure/Session";
import { TypeaheadKeywordHints } from "@/views/components/Typeaheads/Keyword";

interface Props<T>
  extends IFilterComponentProps<T>,
    Omit<ITypeaheadProps, keyof IFilterComponentProps<T> | "loadOptions"> {}

export const KeywordFilter: FC<Props<string>> = memo(({ name, onChange, onApplyFilters, ...rest }) => {
  const { t } = useTranslation("public");
  const { contextCompany } = useContext(UserContext);
  const { authenticated } = useContext(SessionContext);
  const [keywordSearchHistory, setKeywordSearchHistory] = useState<IKeyword[]>([]);
  const [updateSearchHistory, setUpdateSearchHistory] = useToggle(false);
  const baseClass = "keyword-filter";
  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter") {
      if (onApplyFilters) {
        onApplyFilters();
      }
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const keywordsSearchHistoryResponse = await fetchKeywordsSearchHistory({ companyId: contextCompany?.id });
      setKeywordSearchHistory(keywordsSearchHistoryResponse);
    };
    if (authenticated) {
      fetchData();
    }
  }, [authenticated, updateSearchHistory]);

  const handleChange = useOnChangeFilterHandler(name, onChange);
  const handleTypeaheadChange = ({ keyword }: IKeyword) => {
    handleChange(keyword);
  };

  const HeadingMenu = (props: any) => {
    return (
      <SelectComponents.Menu {...props}>
        <Flex padding={["xs"]}>
          <Text color="grey2">{t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.RECENT_SEARCHES")}</Text>
        </Flex>

        {props.children}
      </SelectComponents.Menu>
    );
  };

  const handleDeleteSearchClick = async (e: any, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await deleteKeywordSearchHistory({ keywordId: id, companyId: contextCompany?.id });
    } catch (e) {
      // TODO what if it wont work?
    }
    setUpdateSearchHistory();
  };

  const Option = ({ className, data, ...restProps }: any) => {
    return (
      <SelectComponents.Option {...restProps} className={classNames(className, `${baseClass}__option`)}>
        <Flex direction="row">
          <Flex grow={1} direction="row" innerPadding="sm">
            <Icon icon="search" size="md" color="grey4" />
            <Text>{data.keyword}</Text>
          </Flex>
          {data.id && (
            <Button
              variant="clean"
              icon="close"
              iconColor="grey2"
              onClick={e => {
                handleDeleteSearchClick(e, data.id);
              }}
            />
          )}
        </Flex>
      </SelectComponents.Option>
    );
  };

  const DropdownIndicator = () => null;
  return (
    <div className="filter-input" onKeyPress={handleKeyPress}>
      {rest.value || !authenticated ? (
        <TextInput name={name} onChange={handleChange} type="text" {...rest} />
      ) : (
        <TypeaheadKeywordHints
          labelKey="keyword"
          valueKey="keyword"
          name={name}
          isClearable
          onChange={handleTypeaheadChange}
          isCreatable
          onCreateOption={(input: string) => {
            addKeywordSearchHistory({ companyId: contextCompany?.id }, { keyword: input }).then(() => {
              setUpdateSearchHistory();
              handleChange(input);
            });
          }}
          createOptionPosition="first"
          getNewOptionData={(input: string) => ({
            keyword: input,
          })}
          searchParams={{ onQueryChange: handleChange, companyId: contextCompany?.id }}
          components={{
            Menu: HeadingMenu,
            Option,
            DropdownIndicator,
          }}
          defaultOptions={keywordSearchHistory}
          {...rest}
        />
      )}
    </div>
  );
});
